import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { 
  Users, 
  UserPlus, 
  Trash2, 
  Mail, 
  Lock, 
  User, 
  ArrowLeft, 
  Loader2,
  Shield,
  Calendar,
  LogOut
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_small-biz-loans/artifacts/ojg09jbh_logo.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  
  const [loanOfficers, setLoanOfficers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [newOfficer, setNewOfficer] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: ""
  });

  useEffect(() => {
    fetchLoanOfficers();
  }, []);

  const fetchLoanOfficers = async () => {
    try {
      const response = await axios.get(`${API}/admin/loan-officers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLoanOfficers(response.data.loan_officers || []);
    } catch (error) {
      console.error("Failed to fetch loan officers:", error);
      if (error.response?.status === 403) {
        toast.error("Access denied. Admin privileges required.");
        navigate("/dashboard");
      } else {
        toast.error("Failed to load loan officers");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOfficer = async (e) => {
    e.preventDefault();
    
    if (newOfficer.password !== newOfficer.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (newOfficer.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setCreateLoading(true);
    
    try {
      await axios.post(`${API}/admin/create-loan-officer`, {
        name: newOfficer.name,
        email: newOfficer.email,
        password: newOfficer.password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Loan officer created successfully!");
      setDialogOpen(false);
      setNewOfficer({ name: "", email: "", password: "", confirmPassword: "" });
      fetchLoanOfficers();
    } catch (error) {
      console.error("Failed to create loan officer:", error);
      const errorMessage = error.response?.data?.detail || "Failed to create loan officer";
      toast.error(errorMessage);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteOfficer = async (userId, officerEmail) => {
    if (!window.confirm(`Are you sure you want to delete loan officer ${officerEmail}?`)) {
      return;
    }
    
    setDeleteLoading(userId);
    
    try {
      await axios.delete(`${API}/admin/loan-officer/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Loan officer deleted successfully");
      fetchLoanOfficers();
    } catch (error) {
      console.error("Failed to delete loan officer:", error);
      toast.error("Failed to delete loan officer");
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#0A2540] text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Tzur Finances" className="h-10 rounded" />
              <span className="text-xl font-bold">Admin Dashboard</span>
            </Link>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-white/80">
                <Shield className="h-4 w-4" />
                <span className="text-sm">{user?.email}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:text-white hover:bg-white/10"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Link>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Manage Loan Officers
                </CardTitle>
                <CardDescription>
                  Create and manage loan officer accounts
                </CardDescription>
              </div>
              
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#0A2540] hover:bg-[#0A2540]/90" data-testid="create-officer-btn">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Create Loan Officer
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Create Loan Officer</DialogTitle>
                    <DialogDescription>
                      Create a new loan officer account. They will be able to review and manage loan applications.
                    </DialogDescription>
                  </DialogHeader>
                  
                  <form onSubmit={handleCreateOfficer} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="officer-name">Full Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          id="officer-name"
                          type="text"
                          placeholder="John Doe"
                          className="pl-10"
                          value={newOfficer.name}
                          onChange={(e) => setNewOfficer({ ...newOfficer, name: e.target.value })}
                          required
                          data-testid="officer-name-input"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="officer-email">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          id="officer-email"
                          type="email"
                          placeholder="officer@tzurfinances.com"
                          className="pl-10"
                          value={newOfficer.email}
                          onChange={(e) => setNewOfficer({ ...newOfficer, email: e.target.value })}
                          required
                          data-testid="officer-email-input"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="officer-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          id="officer-password"
                          type="password"
                          placeholder="••••••••"
                          className="pl-10"
                          value={newOfficer.password}
                          onChange={(e) => setNewOfficer({ ...newOfficer, password: e.target.value })}
                          required
                          minLength={6}
                          data-testid="officer-password-input"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="officer-confirm-password">Confirm Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          id="officer-confirm-password"
                          type="password"
                          placeholder="••••••••"
                          className="pl-10"
                          value={newOfficer.confirmPassword}
                          onChange={(e) => setNewOfficer({ ...newOfficer, confirmPassword: e.target.value })}
                          required
                          minLength={6}
                          data-testid="officer-confirm-password-input"
                        />
                      </div>
                    </div>
                    
                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className="bg-[#0A2540] hover:bg-[#0A2540]/90"
                        disabled={createLoading}
                        data-testid="submit-create-officer-btn"
                      >
                        {createLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          "Create Account"
                        )}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : loanOfficers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No loan officers found</p>
                <p className="text-sm text-gray-400 mt-1">
                  Click "Create Loan Officer" to add one
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loanOfficers.map((officer) => (
                      <TableRow key={officer.user_id} data-testid={`officer-row-${officer.user_id}`}>
                        <TableCell className="font-medium">{officer.name}</TableCell>
                        <TableCell>
                          <span className="flex items-center gap-2 text-gray-600">
                            <Mail className="h-4 w-4" />
                            {officer.email}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="flex items-center gap-2 text-gray-500 text-sm">
                            <Calendar className="h-4 w-4" />
                            {formatDate(officer.created_at)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteOfficer(officer.user_id, officer.email)}
                            disabled={deleteLoading === officer.user_id}
                            data-testid={`delete-officer-${officer.user_id}`}
                          >
                            {deleteLoading === officer.user_id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminDashboard;
